import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { Button, Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuBrain,
  LuFileText,
  LuChartLine,
  LuRefreshCw,
  LuScanEye,
  LuShieldCheck,
  LuTarget,
  LuUserCheck,
  LuUsers,
} from 'react-icons/lu';

import AiBrainVisual from '../app/components/AiBrainVisual';
import GradientCTA from '../app/components/GradientCTA';
import HiwHeroWorkflow from '../app/components/HiwHeroWorkflow';
import HeroAnimated from '../app/components/HeroAnimated';
import ProductVideoModal from '../app/components/ProductVideoModal';
import HiwAiFeaturesList from '../app/components/HiwAiFeaturesList';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';
import { config } from '../lib/config';

type ProcessStep = {
  n: string;
  title: string;
  subtitle: string;
  body: string;
  Icon: IconType;
  mockup: ReactNode;
};

const processSteps: ProcessStep[] = [
  {
    n: '01',
    title: 'Understand',
    subtitle: 'We start with you.',
    body: 'We align on your goals, role requirements, team dynamics, and ideal candidate profile to define success.',
    Icon: LuTarget,
    mockup: <Step1Mockup />,
  },
  {
    n: '02',
    title: 'AI-Powered Search',
    subtitle: 'Data finds the right talent.',
    body: 'Our AI scans millions of data points across networks, databases, and market intel to build the strongest, most relevant candidate pool—fast.',
    Icon: LuBrain,
    mockup: <Step2Mockup />,
  },
  {
    n: '03',
    title: 'Human Validation',
    subtitle: 'Experts ensure the perfect fit.',
    body: 'Our domain experts validate, qualify, and engage candidates—going beyond the resume to assess skills, experience, and cultural fit.',
    Icon: LuUsers,
    mockup: <Step3Mockup />,
  },
  {
    n: '04',
    title: 'Exclusive Shortlist',
    subtitle: 'You get only the best.',
    body: 'We deliver a curated, exclusive shortlist of high-caliber candidates—vetted and ready for your interviews.',
    Icon: LuShieldCheck,
    mockup: <Step4Mockup />,
  },
];

const aiFeatures = [
  {
    title: 'Real-Time Market Intelligence',
    body: 'Understand talent availability, compensation trends, and competitor movement.',
    Icon: LuBrain,
  },
  {
    title: 'Predictive Match Scoring',
    body: 'AI ranks candidates based on your unique requirements and success patterns.',
    Icon: LuChartLine,
  },
  {
    title: 'Bias-Free Shortlisting',
    body: 'Objective, data-driven insights reduce bias and expand your access to diverse talent.',
    Icon: LuScanEye,
  },
  {
    title: 'Continuous Learning',
    body: 'Our models learn and improve with every search to get even smarter over time.',
    Icon: LuRefreshCw,
  },
];

export default function HowItWorksPage() {
  const [showProductVideo, setShowProductVideo] = useState(false);

  return (
    <>
      <section className="hiw-hero text-white position-relative">
        <div className="hiw-hero__glow" aria-hidden />
        <Container className="py-5 py-lg-6 position-relative">
          <Row className="align-items-center gy-5">
            <Col lg={6}>
              <HeroAnimated>
                <p data-hero-item className="text-info fw-semibold text-uppercase small mb-3 hiw-process__heading">
                  How it works
                </p>
                <h1 data-hero-item className="display-5 fw-bold mb-2 lh-sm">
                  A smarter way to hire critical talent.
                </h1>
                <p
                  data-hero-item
                  className="hiw-hero__tagline hiw-hero__headline-accent fw-bold mb-4"
                >
                  Powered by AI. Driven by experts.
                </p>
                <p data-hero-item className="lead text-white-50 mb-0">
                  SBR2TH combines the power of AI with deep human expertise
                  <br />
                  to deliver high-caliber talent, quality candidates, faster.
                </p>
              </HeroAnimated>
            </Col>
            <Col lg={6}>
              <RevealSection>
                <HiwHeroWorkflow />
              </RevealSection>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="process" className="hiw-process py-5 py-lg-6">
        <Container>
          <div className="sbr-section-header text-center mb-5 hiw-process__intro">
            <h2 className="sbr-section__title hiw-process__title fw-bold mb-2">Our Proven 4-Step Process</h2>
            <p className="sbr-section__lead hiw-process__subtitle mb-0">Smarter technology. Better outcomes.</p>
            <p className="hiw-process__example-note mb-0">Example Talent Search below.</p>
          </div>

          <div className="position-relative">
            {processSteps.map((step, idx) => (
              <RevealSection key={step.n}>
                <div
                  className={`hiw-step-row ${idx === processSteps.length - 1 ? 'hiw-step-row--last' : ''}`}
                >
                  <div className="hiw-step__timeline">
                    <div className="hiw-step__badge">{step.n}</div>
                    {idx < processSteps.length - 1 ? <div className="hiw-step__line" aria-hidden /> : null}
                  </div>
                  <div className="hiw-step-row__content">
                    <Row className="gy-4 align-items-center">
                      <Col lg={5} className={idx % 2 === 1 ? 'order-lg-2' : ''}>
                        <div className="hiw-step__main">
                          <div className="hiw-step__icon-wrap">
                            <SbrIcon icon={step.Icon} size={26} />
                          </div>
                          <div className="hiw-step__copy">
                            <h3 className="hiw-step__title">{step.title}</h3>
                            <p className="hiw-step__subtitle">{step.subtitle}</p>
                            <p className="hiw-step__body">
                              <em>{step.body}</em>
                            </p>
                          </div>
                        </div>
                      </Col>
                      <Col lg={7} className={idx % 2 === 1 ? 'order-lg-1' : ''}>
                        <div className="hiw-mockup p-3 p-md-4 sbr-card-hover">{step.mockup}</div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </Container>
      </section>

      <section className="hiw-ai-banner text-white py-5 py-lg-6 position-relative">
        <div className="hiw-ai-banner__glow" aria-hidden />
        <Container className="position-relative">
          <Row className="align-items-center gy-5">
            <Col lg={5} className="order-lg-1">
              <RevealSection>
                <p className="hiw-ai-banner__eyebrow text-info fw-semibold text-uppercase small mb-2">
                  AI + Human. Better together.
                </p>
                <h2 className="sbr-section__title fw-bold mb-3">
                  Our <span className="text-gradient-teal-blue">AI engine</span>, your{' '}
                  <span className="text-gradient-teal-blue">competitive advantage</span>.
                </h2>
                <p className="text-white-50 mb-4">
                  SBR2TH.ai augments human expertise with real-time market data, predictive scoring, and continuous
                  learning to deliver a hiring experience that&apos;s faster, smarter, and built for results.
                </p>
                <Button
                  variant="secondary"
                  size="lg"
                  className="hiw-ai-banner__cta fw-semibold"
                  onClick={() => setShowProductVideo(true)}
                >
                  <span className="hiw-ai-banner__cta-label">See SBR2TH.ai in Action</span>
                  <SbrIcon icon={LuArrowRight} className="hiw-ai-banner__cta-icon" size={18} />
                </Button>
              </RevealSection>
            </Col>
            <Col lg={4} className="order-lg-2">
              <RevealSection>
                <AiBrainVisual />
              </RevealSection>
            </Col>
            <Col lg={3} className="order-lg-3">
              <HiwAiFeaturesList items={aiFeatures} />
            </Col>
          </Row>
        </Container>
      </section>

      <GradientCTA
        icon={<i className="bi bi-calendar-event" aria-hidden />}
        title={
          <>
            <div>Ready to experience a better way to hire?</div>
            <div className="sbr-cta__subtitle fw-normal opacity-75 mt-2">Let&apos;s build your next great team.</div>
          </>
        }
      />

      <ProductVideoModal
        show={showProductVideo}
        onHide={() => setShowProductVideo(false)}
        videoUrl={config.productVideoUrl}
        title="See SBR2TH.ai in Action"
      />
    </>
  );
}

function Step1Mockup() {
  return (
    <Row className="g-3">
      <Col md={6}>
        <div className="hiw-mockup__title">Role Brief</div>
        <span className="hiw-mockup__chip mb-2 d-inline-block">Intake</span>
        <ul className="hiw-mockup__list">
          <li>Business objectives</li>
          <li>Role requirements</li>
          <li>Must-have skills</li>
          <li>Nice-to-have skills</li>
          <li>Success profile</li>
        </ul>
      </Col>
      <Col md={6}>
        <div className="hiw-mockup__title">Role Snapshot</div>
        <div className="p-3 rounded-3 bg-light small">
          <div className="fw-bold text-dark mb-2">VP of Operations</div>
          <div className="text-secondary mb-1">
            <strong>Department:</strong> Operations
          </div>
          <div className="text-secondary mb-1">
            <strong>Location:</strong> Chicago, IL
          </div>
          <div className="text-secondary mb-2">
            <strong>Experience:</strong> 12+ years
          </div>
          <span className="badge bg-dark text-white-50 fw-normal">Confidential posting</span>
        </div>
      </Col>
    </Row>
  );
}

const AI_SEARCH_PIPELINE = [
  { stage: 'Discover', sub: 'Scanning', Icon: LuFileText },
  { stage: 'Identify', sub: 'Ranking', Icon: LuBrain },
  { stage: 'Qualify', sub: 'Scoring', Icon: LuShieldCheck },
  { stage: 'Shortlist', sub: 'Curating', Icon: LuUserCheck },
] as const;

function Step2Mockup() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const metrics = [
    { value: '25M+', label: 'Data Sources Scanned', primary: true },
    { value: '1.2M+', label: 'Candidate Profiles' },
    { value: '250+', label: 'Shortlist in Progress' },
    { value: '92%', label: 'Match Accuracy' },
  ];

  useGSAP(
    () => {
      if (!rootRef.current) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        rootRef.current.querySelector('.hiw-ai-search__stage[data-stage="1"]')?.classList.add('hiw-ai-search__stage--active');
        return;
      }

      const stages = rootRef.current.querySelectorAll<HTMLElement>('.hiw-ai-search__stage');
      const metricValues = rootRef.current.querySelectorAll<HTMLElement>('.hiw-ai-search__metric-value');

      const setActive = (index: number) => {
        stages.forEach((stage, j) => {
          stage.classList.toggle('hiw-ai-search__stage--active', j === index);
          const icon = stage.querySelector<HTMLElement>('.hiw-ai-search__stage-icon');
          if (icon) {
            gsap.to(icon, {
              scale: j === index ? 1.08 : 1,
              duration: 0.35,
              ease: 'back.out(1.6)',
              overwrite: 'auto',
            });
          }
        });
        metricValues.forEach((el, j) => {
          gsap.to(el, {
            scale: j === index ? 1.06 : 1,
            opacity: j === index ? 1 : 0.85,
            duration: 0.35,
            overwrite: 'auto',
          });
        });
      };

      setActive(1);

      const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.4 });
      AI_SEARCH_PIPELINE.forEach((_, i) => {
        tl.add(() => setActive(i));
        tl.to({}, { duration: 1.1 });
      });
    },
    { scope: rootRef },
  );

  return (
    <div ref={rootRef} className="hiw-ai-search">
      <div className="hiw-mockup__title mb-3">AI Search in Progress</div>

      <Row className="g-2 hiw-ai-search__metrics">
        {metrics.map((m, i) => (
          <Col xs={6} md={3} key={m.label}>
            <div className="hiw-ai-search__metric">
              <div
                className={`hiw-ai-search__metric-value ${m.primary ? 'hiw-ai-search__metric-value--primary' : ''}`}
                data-metric={i}
              >
                {m.value}
              </div>
              <div className="hiw-ai-search__metric-label">{m.label}</div>
            </div>
          </Col>
        ))}
      </Row>

      <div className="hiw-ai-search__pipeline">
        {AI_SEARCH_PIPELINE.map(({ stage, sub, Icon }, i) => (
          <div key={stage} className="hiw-ai-search__stage" data-stage={i}>
            <div className="hiw-ai-search__stage-icon">
              <SbrIcon icon={Icon} size={16} />
            </div>
            <div className="hiw-ai-search__stage-label">{stage}</div>
            <div className="hiw-ai-search__stage-sub">{sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step3Mockup() {
  const candidates = [
    { initials: 'JM', color: '#2b93f0', name: 'Jordan M.', role: 'VP Operations · 14 yrs', loc: 'Chicago', pct: 95, ring: 'strong', label: 'Strong Match' },
    { initials: 'AL', color: '#00c2ab', name: 'Alex L.', role: 'Director Ops · 11 yrs', loc: 'Dallas', pct: 86, ring: 'good', label: 'Good Match' },
    { initials: 'RK', color: '#64748b', name: 'Riley K.', role: 'Sr. Director · 10 yrs', loc: 'Atlanta', pct: 82, ring: 'good', label: 'Good Match' },
  ];

  return (
    <>
      <div className="hiw-mockup__title">Expert Review Panel</div>
      {candidates.map((c) => (
        <div key={c.name} className="hiw-candidate-row">
          <div className="hiw-avatar" style={{ background: c.color }}>
            {c.initials}
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="fw-semibold small text-dark">{c.name}</div>
            <div className="text-secondary" style={{ fontSize: '0.7rem' }}>
              {c.role} · {c.loc}
            </div>
          </div>
          <div className="text-end">
            <div className={`hiw-match-ring hiw-match-ring--${c.ring}`}>{c.pct}%</div>
            <div className="text-secondary mt-1" style={{ fontSize: '0.62rem' }}>
              {c.label}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function Step4Mockup() {
  const finalists = [
    { initials: 'SC', color: '#2b93f0', name: 'Sam C.', title: 'VP Operations', match: 96 },
    { initials: 'EP', color: '#00c2ab', name: 'Elena P.', title: 'Sr. Director', match: 94 },
    { initials: 'TW', color: '#0a0f1f', name: 'Taylor W.', title: 'Head of Supply Chain', match: 91 },
  ];

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="hiw-mockup__title mb-0">Your Exclusive Shortlist</div>
        <span className="hiw-mockup__chip">Curated</span>
      </div>
      <div className="d-flex flex-column flex-sm-row gap-2">
        {finalists.map((f) => (
          <div key={f.name} className="hiw-shortlist-card">
            <div className="hiw-shortlist-card__avatar" style={{ background: f.color }}>
              {f.initials}
            </div>
            <div className="fw-bold small text-dark">{f.name}</div>
            <div className="text-secondary mb-2" style={{ fontSize: '0.68rem' }}>
              {f.title}
            </div>
            <div className="text-info fw-bold small mb-2">{f.match}% match</div>
            <span className="hiw-shortlist-card__action">View Profile</span>
          </div>
        ))}
      </div>
    </>
  );
}

